import React from 'react';
import { Link as ChakraLink, LinkProps as ChakraLinkProps } from '@chakra-ui/react';
import { Link, LinkProps } from '@tanstack/react-router';

type LinkBaseProps = ChakraLinkProps & { children: React.ReactNode };

type ToLinkProps = LinkBaseProps & {
	to: LinkProps['to'];
	params?: LinkProps['params'];
	search?: LinkProps['search'];
	href?: never;
	onClick?: React.MouseEventHandler<HTMLAnchorElement>;
	target?: string;
	rel?: string;
};

type HrefLinkProps = LinkBaseProps & {
	href: string;
	to?: never;
	onClick?: React.MouseEventHandler<HTMLAnchorElement>;
	target?: string;
	rel?: string;
};

type ButtonLinkProps = LinkBaseProps & {
	onClick: React.MouseEventHandler<HTMLButtonElement>;
	to?: never;
	href?: never;
};

export type AppLinkProps = ToLinkProps | HrefLinkProps | ButtonLinkProps;

/**
 * Reusable link component with consistent styling
 *
 * Uses primary color scheme with underline decoration by default
 * Accepts color and _hover props to override default styling
 * Supports TanStack Router 'to' navigation, standard href, or button-like click behavior
 */
export const AppLink: React.FC<AppLinkProps> = (props) => {
	const { children, color = 'primary', variant = 'underline', _hover = { color: 'primary.600' }, ...rest } = props;

	if ('to' in props && props.to) {
		return (
			<ChakraLink
				asChild
				color={color}
				variant={variant}
				_hover={_hover}
				target={props.target}
				rel={props.rel}
				{...(rest as any)}
			>
				<Link to={props.to} params={props.params as any} search={props.search as any}>
					{children}
				</Link>
			</ChakraLink>
		);
	}

	if ('onClick' in props && !('href' in props) && !('to' in props)) {
		return (
			<ChakraLink
				as="button"
				type="button"
				onClick={props.onClick as React.MouseEventHandler<HTMLButtonElement>}
				color={color}
				variant={variant}
				_hover={_hover}
				{...(rest as any)}
			>
				{children}
			</ChakraLink>
		);
	}

	return (
		<ChakraLink
			href={(props as HrefLinkProps).href}
			color={color}
			variant={variant}
			_hover={_hover}
			target={(props as HrefLinkProps).target}
			rel={(props as HrefLinkProps).rel}
			{...(rest as any)}
		>
			{children}
		</ChakraLink>
	);
};
